import { useCallback, useEffect, useRef } from 'react';

import { useCommunicationDelay } from '../../store/apiHooks/useCommunicationDelay';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useGameStateSlice } from '../../store/slices/game/selectors';

import { GameCommunicationController } from './gameCommunicationController';
import { CoreBetGameCommunicationProcessor } from './processors/CoreBetGameCommunicationProcessor';
import { CoreBetsClosedGameCommunicationProcessor } from './processors/CoreBetsClosedGameCommunicationProcessor';
import { CoreBetsGameCommunicationProcessor } from './processors/CoreBetsGameCommunicationProcessor';
import { CoreBetsOpenGameCommunicationProcessor } from './processors/CoreBetsOpenGameCommunicationProcessor';
import { CoreBetValidationErrorGameCommunicationProcessor } from './processors/CoreBetValidationErrorGameCommunicationProcessor';
import { CoreCancelledGameCommunicationProcessor } from './processors/CoreCancelledGameCommunicationProcessor';
import { CoreCommandGameCommunicationProcessor } from './processors/CoreCommandGameCommunicationProcessor';
import { CoreDealerGameCommunicationProcessor } from './processors/CoreDealerGameCommunicationProcessor';
import { CoreEnableSubmitGameCommunicationProcessor } from './processors/CoreEnableSubmitGameCommunicationProcessor';
import { CoreFreeChipsAvailableGameCommunicationProcessor } from './processors/CoreFreeChipsAvailableGameCommunicationProcessor';
import { CoreGameGameCommunicationProcessor } from './processors/CoreGameGameCommunicationProcessor';
import { CoreGameInitGameCommunicationProcessor } from './processors/CoreGameInitGameCommunicationProcessor';
import { CoreMessageGameCommunicationProcessor } from './processors/CoreMessageGameCommunicationProcessor';
import { CorePromotionsGameCommunicationProcessor } from './processors/CorePromotionsGameCommunicationProcessor';
import { CoreRegulationGameCommunicationProcessor } from './processors/CoreRegulationGameCommunicationProcessor';
import { CoreSessionExpireGameCommunicationProcessor } from './processors/CoreSessionExpireGameCommunicationProcessor';
import { CoreSessionGameCommunicationProcessor } from './processors/CoreSessionGameCommunicationProcessor';
import { CoreStartDealingGameCommunicationProcessor } from './processors/CoreStartDealingGameCommunicationProcessor';
import { CoreSubscribeGameCommunicationProcessor } from './processors/CoreSubscribeGameCommunicationProcessor';
import { CoreSwitchGameCommunicationProcessor } from './processors/CoreSwitchGameCommunicationProcessor';
import { CoreTableClosedGameCommunicationProcessor } from './processors/CoreTableClosedGameCommunicationProcessor';
import { CoreTableCloseNotifyGameCommunicationProcessor } from './processors/CoreTableCloseNotifyGameCommunicationProcessor';
import { CoreThirdPartyErrorGameCommunicationProcessor } from './processors/CoreThirdPartyErrorGameCommunicationProcessor';
import { CoreTimerGameCommunicationProcessor } from './processors/CoreTimerGameCommunicationProcessor';
import { CoreWinGameCommunicationProcessor } from './processors/CoreWinGameCommunicationProcessor';
import { CoreWinnersGameCommunicationProcessor } from './processors/CoreWinnersGameCommunicationProcessor';
import type { GameCommunicationControllerConnectorProps, IGameCommunicationProcessorFactory } from './types';
import { CoreTerminateSessionProcessor } from './processors/CoreTerminateSessionProcessor';

const gameCommunicationProcessors: IGameCommunicationProcessorFactory[] = [
  CoreBetGameCommunicationProcessor,
  CoreBetValidationErrorGameCommunicationProcessor,
  CoreBetsClosedGameCommunicationProcessor,
  CoreBetsOpenGameCommunicationProcessor,
  CoreBetsGameCommunicationProcessor,
  CoreCancelledGameCommunicationProcessor,
  CoreCommandGameCommunicationProcessor,
  CoreDealerGameCommunicationProcessor,
  CoreSessionExpireGameCommunicationProcessor,
  CoreEnableSubmitGameCommunicationProcessor,
  CoreGameInitGameCommunicationProcessor,
  CoreGameGameCommunicationProcessor,
  CoreMessageGameCommunicationProcessor,
  CorePromotionsGameCommunicationProcessor,
  CoreRegulationGameCommunicationProcessor,
  CoreStartDealingGameCommunicationProcessor,
  CoreSubscribeGameCommunicationProcessor,
  CoreSwitchGameCommunicationProcessor,
  CoreTableCloseNotifyGameCommunicationProcessor,
  CoreTableClosedGameCommunicationProcessor,
  CoreThirdPartyErrorGameCommunicationProcessor,
  CoreTimerGameCommunicationProcessor,
  CoreWinGameCommunicationProcessor,
  CoreWinnersGameCommunicationProcessor,
  CoreSessionGameCommunicationProcessor,
  CoreFreeChipsAvailableGameCommunicationProcessor,
  CoreTerminateSessionProcessor
];

const gameCommunicationSenders: any[] = [];

export const GameCommunicationControllerConnector = ({
  gameStore,
  config,
}: GameCommunicationControllerConnectorProps): null => {
  const gameCommunicationControllerRef = useRef(new GameCommunicationController());

  const JSESSIONID = useConfigSlice.JSESSIONID();
  const tableId = useConfigSlice.tableId();
  const tableConfig = useConfigSlice.tableConfig();
  const isGamePaused = useGameStateSlice.getIsGamePaused();
  const gameChannel = useGameStateSlice.gameChannel();
  const gameCommunicationDelay = useCommunicationDelay();
  const isGameAbandoned = useGameStateSlice.getIsGameAbandoned();
  const isUserInfoReady = useGameStateSlice.userInfoReady();

  // Flag which prevents unnecessary connection in case of unavailable game table
  const shouldHaltCommunication = useCallback((): boolean => {
    const isTableClose = tableConfig?.tableClosed;

    return Boolean(isTableClose);
  }, [tableConfig]);

  useEffect(() => {
    // Setting up Processors
    const processors = gameCommunicationProcessors.concat(config?.processors ?? []);
    processors.forEach((processor) => gameCommunicationControllerRef.current.setProcessor(processor, gameStore));

    // Setting up Senders
    const senders = gameCommunicationSenders.concat(config?.senders ?? []);
    // Redundant. Needed only due to invalid senders
    if (gameStore) {
      gameCommunicationControllerRef.current.setStore(gameStore);
    }
    senders.forEach((sender) => gameCommunicationControllerRef.current.setSender(sender));

    if (config?.connectionObserver) {
      gameCommunicationControllerRef.current.setConnectionObserver(config.connectionObserver);
    }
  }, [config, gameStore]);

  useEffect(() => {
    gameCommunicationControllerRef.current.stopStart(isGamePaused);
  }, [isGamePaused]);

  useEffect(() => {
    if (isGameAbandoned) {
      gameCommunicationControllerRef.current.die();
    }
  }, [isGameAbandoned]);

  useEffect(() => {
    if (gameChannel) {
      gameCommunicationControllerRef.current.setInitialized();
    }
  }, [gameChannel]);

  useEffect(() => {
    if (gameCommunicationDelay) {
      gameCommunicationControllerRef.current.setServerMessageDelay(gameCommunicationDelay);
    }
  }, [gameCommunicationDelay]);

  useEffect(() => {
    if (!shouldHaltCommunication() && tableConfig && JSESSIONID && tableId && isUserInfoReady) {
      gameCommunicationControllerRef.current.setCredentials(tableConfig.wsAddress, JSESSIONID, tableId);
      gameCommunicationControllerRef.current.start();
    }
  }, [shouldHaltCommunication, tableConfig, tableId, JSESSIONID, isUserInfoReady]);

  return null;
};
